/**
 * The side-nav model and its pure rules, out of Layout so they are unit-tested:
 * which items a user may see (auth-gated nav) and which counts trail them (D28).
 */
import type { ReactNode } from 'react'
import {
  IconBolt,
  IconBuilding,
  IconClipboardCheck,
  IconFile,
  IconGrid,
  IconHelp,
  IconList,
  IconSliders,
  IconWarning,
} from './carbon/icons'
import { IuvoMark } from './carbon/IuvoMark'
import { Swatch } from './carbon/Swatch'
import type { NavItemCount } from './carbon/types'

/** Which nav count an item carries (D28); the counts come from the pages' own query keys. */
export type CountKey = 'drifts' | 'controls' | 'mfa' | 'pendingOrgs'

export interface NavigationItem {
  name: string
  href: string
  lead: ReactNode
  /** Global Admins only. */
  adminOnly?: boolean
  /**
   * Platform Admins only (iuvotech-org users with GlobalAdmin): a narrower cohort than `adminOnly`, which gates on
   * any Global Admin in any org.
   */
  platformAdminOnly?: boolean
  countKey?: CountKey
}

export interface NavigationGroup {
  label: string
  /** The breadcrumb parent route for pages in this group. */
  home: string
  items: NavigationItem[]
}

// D8: the redirect-only admin entries (Monitored Tenants, Organization
// Management, User Management) are gone from the nav; their routes stay.
// D9: "OPA Validation" is "Policy validation" (UI copy never names OPA).
export const NAVIGATION_GROUPS: NavigationGroup[] = [
  {
    label: 'Posture',
    home: '/dashboard',
    items: [
      { name: 'Command Center', href: '/dashboard', lead: <IuvoMark size={16} /> },
      { name: 'Baseline Compliance', href: '/configuration-drift', lead: <Swatch category="baseline" />, countKey: 'drifts' },
      { name: 'Security Center', href: '/security', lead: <Swatch category="bpa" />, countKey: 'controls' },
      { name: 'User Analytics', href: '/user-insights', lead: <Swatch category="identity" />, countKey: 'mfa' },
    ],
  },
  {
    label: 'Reports',
    home: '/executive-report',
    items: [{ name: 'Executive Report', href: '/executive-report', lead: <IconFile /> }],
  },
  {
    label: 'Administration',
    home: '/admin',
    items: [
      { name: 'Admin Portal', href: '/admin', lead: <IconGrid />, adminOnly: true, countKey: 'pendingOrgs' },
      { name: 'Tenant Management', href: '/tenants', lead: <IconBuilding />, adminOnly: true },
      { name: 'Admin Queue', href: '/admin/queue', lead: <IconList />, adminOnly: true },
      { name: 'Scan Errors', href: '/admin/errors', lead: <IconWarning />, adminOnly: true },
      { name: 'Policy validation', href: '/admin/qa-validation', lead: <IconClipboardCheck />, adminOnly: true },
      { name: 'Ad-Hoc Scans', href: '/admin/scans', lead: <IconBolt />, platformAdminOnly: true },
    ],
  },
  {
    label: 'Workspace',
    home: '/settings',
    items: [
      { name: 'Settings', href: '/settings', lead: <IconSliders /> },
      { name: 'Help & Docs', href: '/help', lead: <IconHelp /> },
    ],
  },
]

// ----------------------------------------------------------------------------
// Breadcrumbs
// ----------------------------------------------------------------------------

/**
 * Pages with no side-nav item: their written name and the nav group they sit under. The `/tcm-lab` name follows
 * the page's own heading ("Run each cutover step from this lab."); UI copy never names the vendor pipeline.
 */
export const PAGE_LABELS: ReadonlyArray<{ path: string; group: string; label: string }> = [
  { path: '/notifications', group: 'Posture', label: 'Notifications' },
  { path: '/tcm-lab', group: 'Administration', label: 'Cutover Lab' },
  { path: '/admin-consent-callback', group: 'Workspace', label: 'Admin consent' },
]

/**
 * Routes that only redirect (legacy links, `/`): during the redirect tick the breadcrumb already names the page
 * they land on.
 */
const REDIRECTS: ReadonlyArray<[from: string, to: string]> = [
  ['/', '/dashboard'],
  ['/drift', '/configuration-drift'],
  ['/baseline-reports', '/configuration-drift'],
  ['/config-as-code', '/configuration-drift'],
  ['/compliance', '/security'],
  ['/monitored-tenants', '/admin'],
  ['/tenant-management', '/admin'],
  ['/organization-management', '/admin'],
  ['/user-management', '/admin'],
]

const underPath = (pathname: string, base: string) => pathname === base || pathname.startsWith(`${base}/`)

/**
 * The breadcrumb for a route: its nav group, then the page's written name from the nav model or PAGE_LABELS. Never a
 * name made from the URL: an unknown path (it redirects to the Command Center) reads as the Command Center.
 */
export function deriveBreadcrumbs(pathname: string): Array<{ label: string; to?: string }> {
  const redirect = REDIRECTS.find(([from]) => (from === '/' ? pathname === '/' : underPath(pathname, from)))
  const path = redirect ? redirect[1] : pathname
  const crumbs = (group: string, label: string) => [{ label: group, to: NAVIGATION_GROUPS.find((g) => g.label === group)?.home }, { label }]

  // The deepest nav item the path sits under (so /admin/queue is Admin Queue, not Admin Portal).
  let best: { group: string; name: string; href: string } | undefined
  for (const group of NAVIGATION_GROUPS) {
    for (const item of group.items) {
      if (underPath(path, item.href) && (!best || item.href.length > best.href.length)) best = { group: group.label, name: item.name, href: item.href }
    }
  }
  const page = PAGE_LABELS.find((p) => underPath(path, p.path))
  if (page && (!best || page.path.length > best.href.length)) return crumbs(page.group, page.label)
  if (best) return crumbs(best.group, best.name)
  return crumbs('Posture', 'Command Center')
}

export interface NavAuth {
  isLoadingUser: boolean
  isGlobalAdmin: boolean
  isPlatformAdmin: boolean
}

/**
 * Drop the items this user may not see, then any group left empty. While the user is loading, every gated item is
 * hidden: no flash of protected links (secure by default).
 */
export function filterNavGroups<G extends NavigationGroup>(groups: readonly G[], { isLoadingUser, isGlobalAdmin, isPlatformAdmin }: NavAuth): G[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.platformAdminOnly) return !isLoadingUser && isPlatformAdmin
        if (item.adminOnly) return !isLoadingUser && isGlobalAdmin
        return true
      }),
    }))
    .filter((group) => group.items.length > 0)
}

export interface NavCountInputs {
  stats?: { activeDrifts?: number | null } | null
  statsLoading: boolean
  summary?: { open?: number | null; byCriticality?: Record<string, number> | null } | null
  summaryLoading: boolean
  insights?: { mailboxClassificationAvailable?: boolean | null; noMfaEnabledUsers?: number | null } | null
  insightsLoading: boolean
  /** Organizations awaiting approval (admins only; 0 otherwise). */
  pendingOrgs: number
}

/**
 * The side-nav counts (D28). Each is hidden while its query loads and when it is zero. Failing controls go Coral
 * when any is critical (SHALL); members without MFA are hidden when `mailboxClassificationAvailable` is false, because
 * the mailbox exclusions behind that count are unreliable then (an undefined flag counts as available).
 */
export function deriveNavCounts(i: NavCountInputs): Partial<Record<CountKey, NavItemCount>> {
  const out: Partial<Record<CountKey, NavItemCount>> = {}
  const drifts = i.stats?.activeDrifts
  if (!i.statsLoading && drifts) {
    out.drifts = { n: drifts, tone: 'neutral', aria: `${drifts} open drift${drifts === 1 ? '' : 's'}` }
  }
  const open = i.summary?.open
  if (!i.summaryLoading && open) {
    const crit = i.summary?.byCriticality?.['SHALL'] ?? 0
    out.controls = {
      n: open,
      tone: crit > 0 ? 'crit' : 'neutral',
      aria: `${open} failing control${open === 1 ? '' : 's'}${crit > 0 ? `, ${crit} critical` : ''}`,
    }
  }
  const noMfa = i.insights?.noMfaEnabledUsers
  if (!i.insightsLoading && i.insights && i.insights.mailboxClassificationAvailable !== false && noMfa && noMfa > 0) {
    out.mfa = { n: noMfa, tone: 'crit', aria: `${noMfa} member${noMfa === 1 ? '' : 's'} without MFA` }
  }
  if (i.pendingOrgs > 0) {
    out.pendingOrgs = {
      n: i.pendingOrgs,
      tone: 'neutral',
      aria: `${i.pendingOrgs} organization${i.pendingOrgs === 1 ? '' : 's'} awaiting approval`,
    }
  }
  return out
}
